/**
 * Anlagentypen API (mandantenübergreifende Stammdaten)
 *
 * Der Anlagentyp ist die Vorlage, aus der beim Hinzufügen einer Anlage ins Projekt kopiert wird:
 * Stoffliste, Betriebsmodi, Auffangraum, F-Gas-Anlage und Ausstattung. Änderungen hier wirken
 * **nicht** rückwirkend in laufende Projekte – die tragen ihre Kopie vom Zeitpunkt des Anlegens.
 *
 * Lesen darf jeder mit Projektrechten, Schreiben nur die Plattform-Administration.
 */
import { api } from "./client";

/** Rückhaltung wassergefährdender Stoffe (ELiA-Formular 11.5) */
export interface AuffangraumFelder {
  groesstes_freisetzbares_volumen_m3?: number | null;
  rueckhaltevolumen_m3?: number | null;
  grundflaeche_m2?: number | null;
  werkstoff_stahl: boolean;
  werkstoff_beton: boolean;
  werkstoff_kunststoff: boolean;
  werkstoff_sonstiges: boolean;
  werkstoff_nummer?: string | null;
  werkstoff_material?: string | null;
  ist_beschichtet: boolean;
  hat_bauwerksfugen: boolean;
  fugenabdichtung_material?: string | null;
  fugen_nachweis_beigefuegt: boolean;
  aufstellung_im_freien: boolean;
  aufstellung_geschuetzt: boolean;
  gefaehrdungsstufe_id?: string | null;
  nach_trws_dwa_a_786: boolean;
  nachweise_beigefuegt: boolean;
}

/** Anlage mit fluorierten Treibhausgasen – die Mittelspannungsschaltanlage (ELiA 15.2) */
export interface FGasFelder {
  fgas_fuellmittel?: string | null;
  fgas_fuellmenge_kg?: number | null;
  fgas_leckage_erkennung: boolean;
  fgas_dichtheitspruefung_intervall_id?: string | null;
}

/** Ausstattungsmerkmale des Anlagentyps (ELiA 4.5, 14.3b.1, 16.1.3, 16.1.9) */
export interface AusstattungFelder {
  bnk_funktionsart?: string | null;
  schallmindernde_ausstattung?: string | null;
  sicherheitstechnische_ausstattung?: string | null;
  auslegungsklasse?: string | null;
}

/** Die technischen Grunddaten plus die drei geteilten Feldgruppen */
export interface AnlagentypFelder
  extends AuffangraumFelder, FGasFelder, AusstattungFelder {
  typ?: string | null;
  rotorradius?: number | null;
  sicherungsradius?: number | null;
  nennleistung?: number | null;
  referenzertrag?: number | null;
  nabenhöhe?: number | null;
}

export interface Anlagentyp extends AnlagentypFelder {
  id: string;
  hersteller_id: string;
  /** Aufgelöster Name des Herstellers */
  hersteller: string;
}

export type AnlagentypCreate = Partial<AnlagentypFelder> & {
  hersteller_id: string;
  typ: string;
};

export type AnlagentypUpdate = Partial<AnlagentypFelder>;

export interface Hersteller {
  id: string;
  name: string;
  /** Wie viele Modelle (Anlagentypen) zu diesem Hersteller gehören */
  anzahl_modelle: number;
}

/** Ein Stoff, Gemisch oder Erzeugnis der Stoffliste (ELiA 3.5 und Folgeformulare) */
export interface StoffFelder {
  bezeichnung?: string | null;
  gesamtmenge?: number | null;
  einheit_id?: string | null;
  art_des_produktes?: string | null;
  komponentenname?: string | null;
  cas_stoff_id?: string | null;
  anteil_prozent?: number | null;
  min_anteil_prozent?: number | null;
  max_anteil_prozent?: number | null;
  avv_abfallschluessel_id?: string | null;
  bemerkung?: string | null;
  /** Die vier Kennzeichen schalten die Folgeformulare frei */
  ist_gefahrstoff: boolean;
  ist_wassergefaehrdend: boolean;
  ist_entstehender_abfall: boolean;
  ist_reach_relevant: boolean;
  /** Gefahrstoff (7.2) */
  verwendung_kg_h?: number | null;
  lagerung_kg?: number | null;
  kennzeichnung?: string | null;
  /** Entstehender Abfall (9.1) */
  entsorgungsverfahren_id?: string | null;
  konsistenz?: string | null;
  anfallhaeufigkeit_pro_jahr?: string | null;
  jahresmenge_t?: number | null;
  /** Wassergefährdend (11.1) */
  awsv_einstufung_id?: string | null;
  aggregatzustand_id?: string | null;
  umgang_art_id?: string | null;
  wassergefaehrdungsklasse?: string | null;
  dichte_g_cm3?: number | null;
  /** REACH (15.1) */
  reach_rolle_id?: string | null;
  reach_jahresmenge_t?: number | null;
  eg_nummer?: string | null;
  index_nummer?: string | null;
  reg_nummer?: string | null;
  ist_registrierter_stoff: boolean;
  ist_anhang_xiv: boolean;
  ist_anhang_xvii: boolean;
  ist_kandidatenstoff: boolean;
  ist_nanoskalig: boolean;
  ist_identifizierte_verwendung: boolean;
}

export interface AnlagentypStoff extends StoffFelder {
  id: string;
  anlagentyp_id: string;
}

export type StoffCreate = Partial<StoffFelder> & { bezeichnung: string };
export type StoffUpdate = Partial<StoffFelder>;

/** Die vermessenen Angaben eines Betriebsmodus – sie stammen vom Anlagentyp (ELiA 16.1.10) */
export interface BetriebsmodusFelder {
  bezeichnung?: string | null;
  rotordrehzahl_pro_min?: number | null;
  oktavpegel_63?: number | null;
  oktavpegel_125?: number | null;
  oktavpegel_250?: number | null;
  oktavpegel_500?: number | null;
  oktavpegel_1000?: number | null;
  oktavpegel_2000?: number | null;
  oktavpegel_4000?: number | null;
  oktavpegel_8000?: number | null;
  schallleistungspegel?: number | null;
}

export interface AnlagentypBetriebsmodus extends BetriebsmodusFelder {
  id: string;
  anlagentyp_id: string;
}

export type BetriebsmodusCreate = Partial<BetriebsmodusFelder> & {
  bezeichnung: string;
};
export type BetriebsmodusUpdate = Partial<BetriebsmodusFelder>;

/** Ein Eintrag einer ELiA-Werteliste */
export interface WertelisteWert {
  id: string;
  /** Der Schlüssel aus der ELiA-Sprachdatei, z. B. "MW" oder "13 01 10*" */
  code: string;
  name: string;
}

/** Ein Eintrag des CAS-Stoffkatalogs – nach Stoffname geschlüsselt, die Nummer darf fehlen */
export interface CasStoff {
  id: string;
  stoffname: string;
  cas_nummer?: string | null;
}

/** Die zehn kurzen Auswahllisten, die ein Dialog gleichzeitig braucht */
export interface Wertelisten {
  einheit: WertelisteWert[];
  entsorgungsverfahren: WertelisteWert[];
  aggregatzustand: WertelisteWert[];
  gefaehrdungsstufe: WertelisteWert[];
  reach_rolle: WertelisteWert[];
  awsv_einstufung: WertelisteWert[];
  rueckhaltung_art: WertelisteWert[];
  dichtheitspruefung_intervall: WertelisteWert[];
  umgang_art: WertelisteWert[];
  einsatzzeit_bezug: WertelisteWert[];
}

export const anlagentypenApi = {
  async getWertelisten(): Promise<Wertelisten> {
    return api.get("/generation-units/wertelisten");
  },

  /** Sucht im AVV-Abfallkatalog (842 Einträge), höchstens 50 Treffer */
  async sucheAvv(q: string): Promise<WertelisteWert[]> {
    return api.get(
      `/generation-units/wertelisten/avv?q=${encodeURIComponent(q)}`,
    );
  },

  /** Sucht im CAS-Stoffkatalog (3.117 Einträge), höchstens 50 Treffer */
  async sucheCas(q: string): Promise<CasStoff[]> {
    return api.get(
      `/generation-units/wertelisten/cas?q=${encodeURIComponent(q)}`,
    );
  },

  async getAnlagentypen(): Promise<Anlagentyp[]> {
    return api.get("/anlagentypen");
  },

  async getHersteller(): Promise<Hersteller[]> {
    return api.get("/anlagentypen/hersteller");
  },

  async createHersteller(name: string): Promise<Hersteller> {
    return api.post("/anlagentypen/hersteller", { name });
  },

  async updateHersteller(
    herstellerId: string,
    name: string,
  ): Promise<Hersteller> {
    return api.put(`/anlagentypen/hersteller/${herstellerId}`, { name });
  },

  /** Schlägt fehl, solange Modelle an diesem Hersteller hängen */
  async deleteHersteller(herstellerId: string): Promise<void> {
    return api.delete(`/anlagentypen/hersteller/${herstellerId}`);
  },

  async createAnlagentyp(data: AnlagentypCreate): Promise<Anlagentyp> {
    return api.post("/anlagentypen", data);
  },

  async updateAnlagentyp(
    anlagentypId: string,
    data: AnlagentypUpdate,
  ): Promise<Anlagentyp> {
    return api.put(`/anlagentypen/${anlagentypId}`, data);
  },

  /** Schlägt fehl, solange Anlagen diesen Typ als Herkunft tragen */
  async deleteAnlagentyp(anlagentypId: string): Promise<void> {
    return api.delete(`/anlagentypen/${anlagentypId}`);
  },

  async getStoffe(anlagentypId: string): Promise<AnlagentypStoff[]> {
    return api.get(`/anlagentypen/${anlagentypId}/stoffe`);
  },

  async createStoff(
    anlagentypId: string,
    data: StoffCreate,
  ): Promise<AnlagentypStoff> {
    return api.post(`/anlagentypen/${anlagentypId}/stoffe`, data);
  },

  async updateStoff(
    stoffId: string,
    data: StoffUpdate,
  ): Promise<AnlagentypStoff> {
    return api.put(`/anlagentypen/stoffe/${stoffId}`, data);
  },

  async deleteStoff(stoffId: string): Promise<void> {
    return api.delete(`/anlagentypen/stoffe/${stoffId}`);
  },

  async getBetriebsmodi(
    anlagentypId: string,
  ): Promise<AnlagentypBetriebsmodus[]> {
    return api.get(`/anlagentypen/${anlagentypId}/betriebsmodi`);
  },

  async createBetriebsmodus(
    anlagentypId: string,
    data: BetriebsmodusCreate,
  ): Promise<AnlagentypBetriebsmodus> {
    return api.post(`/anlagentypen/${anlagentypId}/betriebsmodi`, data);
  },

  async updateBetriebsmodus(
    modusId: string,
    data: BetriebsmodusUpdate,
  ): Promise<AnlagentypBetriebsmodus> {
    return api.put(`/anlagentypen/betriebsmodi/${modusId}`, data);
  },

  async deleteBetriebsmodus(modusId: string): Promise<void> {
    return api.delete(`/anlagentypen/betriebsmodi/${modusId}`);
  },
};
